// src/api/features/suppliers/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createSupplier,
  updateSupplier,
  deleteSupplier,
  approveSupplier,
  rejectSupplier,
  disableSupplierUser,
} from "./api";

import type {
  SupplierResult,
  SupplierWithRelations,
  PaginatedSuppliers,
  CreateSupplierBody,
  UpdateSupplierBody,
} from "./types";

import { SuppliersKeys } from "./queries";

/* =====================================
 * Local helper types (for mutation context)
 * ===================================== */

type SuppliersListSnapshot = {
  key: readonly unknown[];
  data: PaginatedSuppliers | undefined;
};

type UpdateSupplierContext = {
  prevDetail?: SupplierWithRelations;
  prevLists?: SuppliersListSnapshot[];
};

type DeleteSupplierContext = {
  prevLists?: SuppliersListSnapshot[];
};
type ApproveSupplierContext = {
  prevDetail?: SupplierWithRelations;
  prevLists?: SuppliersListSnapshot[];
};
type RejectSupplierContext = ApproveSupplierContext;
type DisableSupplierUserContext = {
  prevDetail?: SupplierWithRelations;
  prevLists?: SuppliersListSnapshot[];
};

/* =====================================
 * Supplier CRUD mutations
 * ===================================== */

/** 🔹 Create supplier (deep payload) */
export function useCreateSupplierMutation() {
  const qc = useQueryClient();

  return useMutation<
    { supplier: SupplierResult; ownerUserId: number },
    unknown,
    CreateSupplierBody
  >({
    mutationFn: (payload: CreateSupplierBody) => createSupplier(payload),

    onSuccess: () => {
      // We only invalidate lists; detail will be fetched when needed.
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}

/** 🔹 Update supplier (deep update) */
export function useUpdateSupplierMutation() {
  const qc = useQueryClient();

  return useMutation<
    SupplierResult,
    unknown,
    { id: number | string; data: UpdateSupplierBody },
    UpdateSupplierContext
  >({
    mutationFn: ({ id, data }) => updateSupplier(id, data),

    async onMutate({ id, data }) {
      // Cancel ongoing queries for this supplier + all lists
      await Promise.all([
        qc.cancelQueries({ queryKey: SuppliersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: SuppliersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;

      const prevLists: SuppliersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: SuppliersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedSuppliers | undefined,
        }));

      // Optimistic patch: detail (deep)
      if (prevDetail) {
        qc.setQueryData(
          SuppliersKeys.detail(id),
          {
            ...prevDetail,
            ...data,
          } as SupplierWithRelations
        );
      }

      // Optimistic patch: list rows (base)
      for (const snap of prevLists) {
        const list = snap.data;
        if (!list?.items) continue;

        const patched = list.items.map((s) =>
          s.id === Number(id) ? ({ ...s, ...data } as SupplierResult) : s
        );

        qc.setQueryData<PaginatedSuppliers>(snap.key, {
          ...list,
          items: patched,
        });
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _vars, ctx) {
      if (!ctx) return;

      // Roll back detail
      if (ctx.prevDetail) {
        qc.setQueryData(
          SuppliersKeys.detail(ctx.prevDetail.id),
          ctx.prevDetail
        );
      }

      // Roll back lists
      for (const snap of ctx.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updatedBase, { id }) {
      // Merge updated base fields into existing deep cache if present
      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;

      if (prevDetail) {
        qc.setQueryData(
          SuppliersKeys.detail(id),
          {
            ...prevDetail,
            ...updatedBase,
          } as SupplierWithRelations
        );
      } else {
        // If deep wasn't cached, just invalidate so it refetches
        qc.invalidateQueries({ queryKey: SuppliersKeys.detail(id) });
      }
    },

    onSettled(_res, _err, { id }) {
      qc.invalidateQueries({ queryKey: SuppliersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}

/** 🔹 Delete supplier */
export function useDeleteSupplierMutation() {
  const qc = useQueryClient();

  return useMutation<
    { message: string },
    unknown,
    number | string,
    DeleteSupplierContext
  >({
    mutationFn: (id: number | string) => deleteSupplier(id),

    async onMutate(id) {
      await qc.cancelQueries({ queryKey: SuppliersKeys.all });

      const numericId = Number(id);
      const prevLists: SuppliersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: SuppliersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedSuppliers | undefined,
        }));

      // Optimistically remove from all lists
      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;

        qc.setQueryData<PaginatedSuppliers>(snap.key, {
          ...data,
          items: data.items.filter((s) => s.id !== numericId),
          total: Math.max(0, (data.total ?? 1) - 1),
        });
      }

      // Drop detail cache for this supplier
      qc.removeQueries({ queryKey: SuppliersKeys.detail(id) });

      return { prevLists };
    },

    onError(_err, _id, ctx) {
      // Roll back lists
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}

/** 🔹 Approve supplier registration */
export function useApproveSupplierMutation() {
  const qc = useQueryClient();

  return useMutation<
    SupplierResult,
    unknown,
    number | string,
    ApproveSupplierContext
  >({
    mutationFn: (id) => approveSupplier(id),

    async onMutate(id) {
      await Promise.all([
        qc.cancelQueries({ queryKey: SuppliersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: SuppliersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;

      const prevLists: SuppliersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: SuppliersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedSuppliers | undefined,
        }));

      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;
        qc.setQueryData<PaginatedSuppliers>(snap.key, {
          ...data,
          items: data.items.map((s) =>
            s.id === Number(id) ? ({ ...s, status: "APPROVED" } as SupplierResult) : s
          ),
        });
      }

      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          status: "APPROVED",
        } as SupplierWithRelations);
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _id, ctx) {
      if (ctx?.prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(ctx.prevDetail.id), ctx.prevDetail);
      }
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, id) {
      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;
      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          ...updated,
        } as SupplierWithRelations);
      }
    },

    onSettled(_res, _err, id) {
      qc.invalidateQueries({ queryKey: SuppliersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}

/** 🔹 Reject supplier registration */
export function useRejectSupplierMutation() {
  const qc = useQueryClient();

  return useMutation<
    SupplierResult,
    unknown,
    number | string,
    RejectSupplierContext
  >({
    mutationFn: (id) => rejectSupplier(id),

    async onMutate(id) {
      await Promise.all([
        qc.cancelQueries({ queryKey: SuppliersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: SuppliersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;

      const prevLists: SuppliersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: SuppliersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedSuppliers | undefined,
        }));

      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;
        qc.setQueryData<PaginatedSuppliers>(snap.key, {
          ...data,
          items: data.items.map((s) =>
            s.id === Number(id) ? ({ ...s, status: "REJECTED" } as SupplierResult) : s
          ),
        });
      }

      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          status: "REJECTED",
        } as SupplierWithRelations);
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _id, ctx) {
      if (ctx?.prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(ctx.prevDetail.id), ctx.prevDetail);
      }
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, id) {
      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;
      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          ...updated,
        } as SupplierWithRelations);
      }
    },

    onSettled(_res, _err, id) {
      qc.invalidateQueries({ queryKey: SuppliersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}

/** 🔹 Disable approved supplier login user */
export function useDisableSupplierUserMutation() {
  const qc = useQueryClient();

  return useMutation<
    SupplierResult,
    unknown,
    number | string,
    DisableSupplierUserContext
  >({
    mutationFn: (id) => disableSupplierUser(id),

    async onMutate(id) {
      await Promise.all([
        qc.cancelQueries({ queryKey: SuppliersKeys.detail(id) }),
        qc.cancelQueries({ queryKey: SuppliersKeys.all }),
      ]);

      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;

      const prevLists: SuppliersListSnapshot[] = qc
        .getQueryCache()
        .findAll({ queryKey: SuppliersKeys.all })
        .map((q) => ({
          key: q.queryKey,
          data: q.state.data as PaginatedSuppliers | undefined,
        }));

      for (const snap of prevLists) {
        const data = snap.data;
        if (!data?.items) continue;
        qc.setQueryData<PaginatedSuppliers>(snap.key, {
          ...data,
          items: data.items.map((s) =>
            s.id === Number(id) ? ({ ...s, userId: null } as SupplierResult) : s
          ),
        });
      }

      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          userId: null,
        } as SupplierWithRelations);
      }

      return { prevDetail, prevLists };
    },

    onError(_err, _id, ctx) {
      if (ctx?.prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(ctx.prevDetail.id), ctx.prevDetail);
      }
      for (const snap of ctx?.prevLists ?? []) {
        qc.setQueryData(snap.key, snap.data);
      }
    },

    onSuccess(updated, id) {
      const prevDetail = qc.getQueryData(
        SuppliersKeys.detail(id)
      ) as SupplierWithRelations | undefined;
      if (prevDetail) {
        qc.setQueryData(SuppliersKeys.detail(id), {
          ...prevDetail,
          ...updated,
        } as SupplierWithRelations);
      }
    },

    onSettled(_res, _err, id) {
      qc.invalidateQueries({ queryKey: SuppliersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: SuppliersKeys.all });
    },
  });
}
